/**
 * PendingApprovalSection — leadership/admin-only list of HubSpot-sourced projects awaiting approval.
 *
 * Fetches GET /api/projects?status=pending_approval and renders one card per pending deal with the
 * deal name, Account Executive (T-12 — the HubSpot owner only when no AE is known), SOW estimate and
 * time-since-request. Deal amount / AWS segment are deliberately never shown (T-10). Each card has Approve (opens the
 * ApprovalModal) and Reject (opens a reason dialog) actions. Renders nothing when there are no
 * pending projects, so it is invisible on a normal Projects page.
 */
import { useMemo, useState } from "react";
import { toast } from "sonner";
import { Clock, Loader2, UserRound } from "lucide-react";

import type { ProjectSummary } from "@/types";
import type { OscarIntake } from "@/types/intake";
import { useProjects, useRejectProject } from "@/hooks/useProjects";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { ApprovalModal } from "@/components/projects/ApprovalModal";
import { AccountExecutiveLabel } from "@/components/intakes/AccountExecutiveLabel";

/** Human-readable "x ago" from an ISO timestamp. */
function timeAgo(iso: string | null | undefined): string {
  if (!iso) return "—";
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return "—";
  const secs = Math.max(0, Math.floor((Date.now() - then) / 1000));
  if (secs < 60) return "just now";
  const mins = Math.floor(secs / 60);
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

function RejectDialog({
  project,
  open,
  onOpenChange,
}: {
  project: ProjectSummary;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}): JSX.Element {
  const reject = useRejectProject(project.jira_key);
  const [reason, setReason] = useState("");

  const handleReject = async (): Promise<void> => {
    if (!reason.trim()) return;
    try {
      await reject.mutateAsync({ reason: reason.trim() });
      toast.success("Project rejected");
      setReason("");
      onOpenChange(false);
    } catch {
      toast.error("Failed to reject project");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Reject “{project.title}”?</DialogTitle>
          <DialogDescription>
            Provide a reason. The project is marked rejected and a Slack
            notification is posted.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-2 py-1">
          <Label htmlFor="reject-reason">Reason *</Label>
          <Textarea
            id="reject-reason"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="e.g. Duplicate deal / not a delivery engagement"
            rows={3}
          />
        </div>
        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={reject.isPending}
          >
            Cancel
          </Button>
          <Button
            variant="danger"
            onClick={handleReject}
            disabled={reject.isPending || !reason.trim()}
          >
            {reject.isPending && (
              <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
            )}
            {reject.isPending ? "Rejecting…" : "Reject"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function PendingApprovalSection(): JSX.Element | null {
  const { data } = useProjects({ status: "pending_approval", limit: 50 });
  // The list endpoint returns the Oscar intake columns (V057 AE + project_summary) on these rows;
  // the shared ProjectSummary type does not declare them, so read the rows as intakes.
  const pending = useMemo<OscarIntake[]>(
    () => (data?.projects ?? []) as OscarIntake[],
    [data],
  );

  const [approveTarget, setApproveTarget] = useState<OscarIntake | null>(null);
  const [rejectTarget, setRejectTarget] = useState<ProjectSummary | null>(null);

  if (pending.length === 0) return null;

  return (
    <section aria-label="Projects pending approval" className="space-y-3">
      <div className="flex items-center gap-2">
        <h2 className="text-lg font-semibold text-text-primary">
          Pending Approval
        </h2>
        <Badge variant="warning">{pending.length}</Badge>
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
        {pending.map((p) => (
          <Card
            key={p.id}
            className="border-amber-300/60 dark:border-amber-500/30"
          >
            <CardContent className="space-y-3 p-5">
              <div className="min-w-0">
                <h3 className="truncate font-semibold text-text-primary">
                  {p.title}
                </h3>
                <span className="font-mono text-xs text-text-secondary">
                  {p.jira_key}
                </span>
              </div>

              <dl className="space-y-1.5 text-sm">
                <div className="flex items-center gap-2 text-text-secondary">
                  <UserRound
                    className="h-3.5 w-3.5 shrink-0"
                    aria-hidden="true"
                  />
                  <span className="truncate">
                    <AccountExecutiveLabel intake={p} />
                  </span>
                </div>
                <div className="flex items-center gap-2 text-text-secondary">
                  <Clock className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
                  <span>Requested {timeAgo(p.approval_requested_at)}</span>
                </div>
                <div className="text-text-secondary">
                  SOW estimate:{" "}
                  {p.sow_hours != null ? `${p.sow_hours} hrs` : "TBD"}
                </div>
              </dl>

              <div className="flex items-center gap-2 pt-1">
                <Button
                  size="sm"
                  className="flex-1"
                  onClick={() => setApproveTarget(p)}
                >
                  Approve
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  className="flex-1"
                  onClick={() => setRejectTarget(p)}
                >
                  Reject
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <ApprovalModal
        open={approveTarget !== null}
        onOpenChange={(o) => !o && setApproveTarget(null)}
        project={approveTarget}
      />
      {rejectTarget && (
        <RejectDialog
          project={rejectTarget}
          open={rejectTarget !== null}
          onOpenChange={(o) => !o && setRejectTarget(null)}
        />
      )}
    </section>
  );
}

export default PendingApprovalSection;
